/* eslint react/prop-types: 0 */
import { useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { t } from '@lingui/macro';

import { COLUMN_TYPES } from '../data-table/DataTableColumns';
import { useQueryLoader } from '../data-table/Loaders';
import { FORMATTER_TYPES, DEFAULT_INVALID_VALUE } from '../../utilities/FormattingUtilities';
import { useAgent, agentFilterId, testFilterId } from '../filters';
import { MaterialUiIcon } from '../icon';
import ReportingDataTable from '../data-table/ReportingDataTable';
import { NATIVE_TEST, NATIVE_TEST_TYPE_GROUPS } from '../../utilities/TestTypeConstants';
import useNg1DrillDownService from '../../services/Ng1DrillDownService';
import { Ng1DrillDownButton } from '../ng1-drill-down/Ng1DrillDownButton';

const dataSource = {
    url: '/query/table',
    params: {
        type: 'agent,test',
        entityFields: 'name,tags',
        metrics: ['status', 'availPercent'],
        trends: ['status'],
    },
};

const useStyles = makeStyles((theme) => ({
    tabTable: {
        paddingTop: theme.spacing(1),

        '@media print': {
            paddingTop: 0,
        },
    },
}));

function getInterface(row) {
    const testType = row?.test?.testType;
    const testTypeName = testType?.name;
    const testTypeGroup = testType?.group;

    // if this is a VoipPulseToPhone test, we do not show the interface type in Guardians. We will fix this in the first 3.6 patch.
    if (testTypeGroup === NATIVE_TEST_TYPE_GROUPS.VOIP && testTypeName === NATIVE_TEST.VoipPulseToPhone) {
        return DEFAULT_INVALID_VALUE;
    }
    return `${row?.test?.adapterType === 'wireless' ? 'Wireless' : 'Wired'}`;
}

function getColumns(agent, hasNg1DrillDownPrivilege) {
    return [
        {
            type: COLUMN_TYPES.ICON,
            title: t`Type`,
            mapping: 'test.testType.details.icon',
            sortProperty: 'test.testType',
            sortable: true,
            width: 74,
            csvFormatter: (value, row) => row?.test?.typeNode?.details?.title,
            icons: [
                {
                    getIcon: (row) => {
                        const { icon } = row?.test?.typeNode?.details || {};
                        return <MaterialUiIcon icon={icon} />;
                    },
                    getTooltip: (row) => row?.test?.typeNode?.details?.title,
                },
            ],
        },
        {
            type: COLUMN_TYPES.DRILLINLINK,
            title: t`Service Test`,
            sortable: true,
            mapping: 'test.name',
            width: 250,
            link: {
                target: '/test-detail',
                mappedFilterParams: {
                    [testFilterId]: 'test',
                },
                excludedFilterParams: [agentFilterId],
            },
        },
        {
            title: t`Interface`,
            width: 80,
            mapper: (row) => getInterface(row),
        },
        {
            type: COLUMN_TYPES.NUMERIC,
            title: t`Availability`,
            width: 115,
            sortable: true,
            mapping: 'availPercent',
            formatter: FORMATTER_TYPES.PERCENT,
        },
        {
            type: COLUMN_TYPES.STATUSTREND,
            title: t`Status Trend`,
            sortable: true,
            sortProperty: 'status',
            mapping: 'trends.status.data',
        },
        {
            type: COLUMN_TYPES.TESTLOGLINK,
            mapping: 'test',
            width: 110,
            uiOnly: true,
            link: {
                target: '/test-log',
                mappedFilterParams: {
                    [testFilterId]: 'test',
                },
            },
        },
        ...(hasNg1DrillDownPrivilege()
            ? [
                  {
                      type: COLUMN_TYPES.ICON,
                      mapping: 'agent',
                      width: 30,
                      uiOnly: true,
                      icons: [
                          {
                              getIcon: (row) => {
                                  return <Ng1DrillDownButton agent={agent} test={row?.test} />;
                              },
                              getTooltip: () => t`Launch nGeniusONE Smart Edge Service Monitor`,
                          },
                      ],
                  },
              ]
            : []),
    ];
}

const sorters = [{ property: 'status', direction: 'desc' }];

export default function AgentTests(props) {
    const agent = useAgent();
    const { hasNg1DrillDownPrivilege } = useNg1DrillDownService();
    const loader = useQueryLoader(dataSource);
    const columns = useMemo(() => getColumns(agent, hasNg1DrillDownPrivilege), [agent, hasNg1DrillDownPrivilege]);
    const classes = useStyles();

    return (
        <ReportingDataTable
            className={classes.tabTable}
            panelProps={props}
            loader={loader}
            columns={columns}
            sorters={sorters}
            deletedMapping="test.deleted"
        />
    );
}
